import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  BellRing,
  Copy,
  Download,
  ExternalLink,
  Eye,
  MoreHorizontal,
  Pencil,
  Send,
  Trash2,
  Wallet,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ConfirmDeleteDialog from '@/components/admin/accounting/ConfirmDeleteDialog';
import InvoiceEmailDialog from '@/components/admin/accounting/InvoiceEmailDialog';
import InvoiceFormDialog from '@/components/admin/accounting/InvoiceFormDialog';
import InvoicePaymentDialog from '@/components/admin/accounting/InvoicePaymentDialog';
import InvoicePdfDialog from '@/components/admin/accounting/InvoicePdfDialog';
import { accountingError } from '@/lib/accounting';
import {
  deleteAccountingInvoice,
  duplicateAccountingInvoice,
  issueAccountingInvoice,
  reopenAccountingInvoice,
  sendAccountingInvoiceReminder,
  voidAccountingInvoice,
} from '@/lib/api';

/**
 * Everything a person can do to one invoice, in one place.
 *
 * The list and the invoice page offer the same actions and had no business
 * implementing them twice: this hook owns the dialogs and the requests, and
 * both pages mount its `dialogs` node and call into it. A change to what
 * "Duplicate" means then happens once.
 *
 * Every action here works. Nothing is rendered as a decorative entry that
 * explains itself when clicked — an action a person cannot take is either
 * absent, or present and disabled with the reason underneath it.
 */

export function invoiceDetailPath(ref) {
  return `/admin/accounting/sales/invoices/${encodeURIComponent(ref)}`;
}

/** Where an invoice that this module does not own is actually edited. */
export function documentOwnerPath(invoice) {
  if (invoice?.student_id) return `/admin/students/${invoice.student_id}`;
  if (invoice?.application_id) return `/admin/applications/${invoice.application_id}`;
  return '';
}

export function useInvoiceWorkflow({ reload, customers, items, taxes, paymentMethods }) {
  const [editFor, setEditFor] = useState(null);
  const [paymentFor, setPaymentFor] = useState(null);
  const [emailFor, setEmailFor] = useState(null);
  const [pdfFor, setPdfFor] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [busyRef, setBusyRef] = useState('');

  const refresh = useCallback(async () => reload?.(), [reload]);

  const run = useCallback(
    async (invoice, action, work, message) => {
      setBusyRef(`${invoice.ref}:${action}`);
      try {
        const result = await work();
        toast.success(typeof message === 'function' ? message(result) : message);
        await refresh();
        return result;
      } catch (err) {
        toast.error(accountingError(err, 'That action could not be completed.'));
        return null;
      } finally {
        setBusyRef('');
      }
    },
    [refresh]
  );

  const actions = {
    edit: setEditFor,
    payment: setPaymentFor,
    email: setEmailFor,
    pdf: setPdfFor,
    remove: setDeleteFor,
    reopen: (invoice) => {
      if (!window.confirm('Return this invoice to draft? Saved payments and sending history remain. Confirm it again when ready.')) return;
      return run(invoice, 'reopen', () => reopenAccountingInvoice(invoice.ref), 'Invoice returned to draft.');
    },
    issue: (invoice) =>
      run(invoice, 'issue', () => issueAccountingInvoice(invoice.ref), `Invoice ${invoice.number} issued.`),
    voidInvoice: (invoice) =>
      run(invoice, 'void', () => voidAccountingInvoice(invoice.ref), `Invoice ${invoice.number} voided.`),
    remind: (invoice) =>
      run(
        invoice,
        'remind',
        () => sendAccountingInvoiceReminder(invoice.ref, {}),
        `Reminder sent to ${invoice.customer?.email}.`
      ),
    // The copy is opened for review rather than left in the list, because a
    // duplicate always needs a look before it is sent to anyone.
    duplicate: async (invoice, onDuplicated) => {
      const result = await run(
        invoice,
        'duplicate',
        () => duplicateAccountingInvoice(invoice.ref),
        (created) => `Invoice ${created.data.number} created as a draft copy of ${invoice.number}.`
      );
      if (result?.data) onDuplicated?.(result.data);
      return result;
    },
  };

  const confirmDelete = async () => {
    setBusyRef(`${deleteFor.ref}:delete`);
    try {
      await deleteAccountingInvoice(deleteFor.ref);
      toast.success(`Invoice ${deleteFor.number} deleted.`);
      setDeleteFor(null);
      await refresh();
    } catch (err) {
      toast.error(accountingError(err, 'That invoice could not be deleted.'));
    } finally {
      setBusyRef('');
    }
  };

  const dialogs = (
    <>
      <InvoiceFormDialog
        open={Boolean(editFor)}
        onOpenChange={(next) => !next && setEditFor(null)}
        mode="edit"
        invoice={editFor}
        customers={customers}
        items={items}
        taxes={taxes}
        onSaved={refresh}
      />
      <InvoicePaymentDialog
        invoice={paymentFor}
        open={Boolean(paymentFor)}
        onOpenChange={(next) => !next && setPaymentFor(null)}
        methods={paymentMethods}
        onRecorded={refresh}
      />
      <InvoiceEmailDialog
        invoice={emailFor}
        open={Boolean(emailFor)}
        onOpenChange={(next) => !next && setEmailFor(null)}
        onSent={refresh}
      />
      <InvoicePdfDialog
        invoice={pdfFor}
        open={Boolean(pdfFor)}
        onOpenChange={(next) => !next && setPdfFor(null)}
      />
      {/* The consequence is the server's own sentence: it is built from the
          payment rows the deletion will actually remove, so it names the
          amount and the person rather than describing what might happen. */}
      <ConfirmDeleteDialog
        open={Boolean(deleteFor)}
        onOpenChange={(next) => !next && setDeleteFor(null)}
        title="Delete this invoice?"
        detail={
          deleteFor
            ? `${deleteFor.number} · ${deleteFor.customer?.name} · ${deleteFor.total_display}`
            : ''
        }
        consequence={deleteFor?.delete_effect?.cascade || ''}
        confirmLabel="Delete invoice"
        busy={busyRef.endsWith(':delete')}
        onConfirm={confirmDelete}
      />
    </>
  );

  return { actions, dialogs };
}

/**
 * Whether each action is available on this invoice, and why not when it is not.
 *
 * The frontend answers this so a menu can explain itself. The server answers it
 * again on every route, which is the answer that counts.
 */
export function invoiceCapabilities(invoice, permissions) {
  const can = permissions || {};
  const accounting = invoice?.origin === 'accounting';
  const draft = invoice?.status === 'draft';
  const voided = invoice?.status === 'void';

  return {
    edit: {
      allowed: Boolean(can.edit_invoice) && !voided,
      elsewhere: '',
      reason: voided
          ? 'A voided invoice is the record of a withdrawn claim, so it is kept as it was.'
          : 'Each revision keeps the invoice number, saved payments and earlier contents.',
    },
    duplicate: { allowed: Boolean(can.duplicate_invoice) },
    payment: {
      allowed:
        Boolean(can.record_invoice_payment) &&
        ['sent', 'partial', 'overdue'].includes(invoice?.status),
      reason: draft
        ? 'Issue this invoice before recording a payment against it.'
        : voided
          ? 'A voided invoice cannot take a payment.'
          : 'This invoice is settled.',
    },
    email: { allowed: Boolean(can.send_invoice) && !voided && !(draft && !accounting) },
    remind: {
      allowed:
        Boolean(can.send_invoice_reminder) &&
        ['sent', 'partial', 'overdue'].includes(invoice?.status) &&
        Boolean(invoice?.customer?.email),
    },
    pdf: { allowed: Boolean(can.export_invoice_pdf) },
    issue: { allowed: Boolean(can.edit_invoice) && draft },
    reopen: { allowed: Boolean(can.edit_invoice) && !draft && (!voided || Boolean(can.void_invoice)) },
    voidInvoice: { allowed: Boolean(can.void_invoice) && accounting && !voided },
    remove: {
      allowed: Boolean(can.delete_invoice) && invoice?.delete_effect?.allowed !== false,
      reason: invoice?.delete_effect?.reason || '',
    },
  };
}

/**
 * The row menu.
 *
 * Deliberately no Print entry: exporting the PDF is the same job done once,
 * and a browser print of a table row is not an invoice.
 */
export default function InvoiceActionsMenu({ invoice, permissions, actions, onDuplicated }) {
  const navigate = useNavigate();
  const able = invoiceCapabilities(invoice, permissions);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="size-8 rounded-full p-0"
          aria-label={`Actions for invoice ${invoice.number}`}
        >
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuItem onSelect={() => navigate(invoiceDetailPath(invoice.ref))}>
          <Eye className="mr-2 size-4" />
          View
        </DropdownMenuItem>

        {able.edit.elsewhere ? (
          <DropdownMenuItem asChild>
            <Link to={able.edit.elsewhere}>
              <ExternalLink className="mr-2 size-4" />
              Edit on the {invoice.student_id ? 'student' : 'application'}
            </Link>
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem disabled={!able.edit.allowed} onSelect={() => actions.edit(invoice)}>
            <Pencil className="mr-2 size-4" />
            Edit
          </DropdownMenuItem>
        )}
        {!able.edit.allowed && !able.edit.elsewhere && permissions?.edit_invoice && (
          <p className="max-w-[15rem] px-2 pb-1.5 pt-0.5 text-[11px] leading-snug text-[#062B1A]/45">
            {able.edit.reason}
          </p>
        )}

        {able.duplicate.allowed && (
          <DropdownMenuItem onSelect={() => actions.duplicate(invoice, onDuplicated)}>
            <Copy className="mr-2 size-4" />
            Duplicate
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        {able.reopen.allowed && <DropdownMenuItem onSelect={() => actions.reopen(invoice)}>Return to draft</DropdownMenuItem>}
        {able.issue.allowed && (
          <DropdownMenuItem onSelect={() => actions.issue(invoice)}>
            <Send className="mr-2 size-4" />
            Issue
          </DropdownMenuItem>
        )}
        {permissions?.record_invoice_payment && (
          <>
            <DropdownMenuItem
              disabled={!able.payment.allowed}
              onSelect={() => actions.payment(invoice)}
            >
              <Wallet className="mr-2 size-4" />
              Record payment
            </DropdownMenuItem>
            {!able.payment.allowed && (
              <p className="max-w-[15rem] px-2 pb-1.5 pt-0.5 text-[11px] leading-snug text-[#062B1A]/45">
                {able.payment.reason}
              </p>
            )}
          </>
        )}
        {able.email.allowed && (
          <DropdownMenuItem onSelect={() => actions.email(invoice)}>
            <Send className="mr-2 size-4" />
            {invoice.send_count > 0 ? 'Resend invoice' : 'Send invoice'}
          </DropdownMenuItem>
        )}
        {able.remind.allowed && (
          <DropdownMenuItem onSelect={() => actions.remind(invoice)}>
            <BellRing className="mr-2 size-4" />
            Send payment reminder
          </DropdownMenuItem>
        )}
        {able.pdf.allowed && (
          <DropdownMenuItem onSelect={() => actions.pdf(invoice)}>
            <Download className="mr-2 size-4" />
            Export as PDF
          </DropdownMenuItem>
        )}

        {(able.voidInvoice.allowed || permissions?.delete_invoice) && <DropdownMenuSeparator />}
        {able.voidInvoice.allowed && (
          <DropdownMenuItem
            onSelect={() => actions.voidInvoice(invoice)}
            className="text-red-700 focus:text-red-700"
          >
            Void
          </DropdownMenuItem>
        )}
        {permissions?.delete_invoice && (
          <>
            <DropdownMenuItem
              disabled={!able.remove.allowed}
              onSelect={() => actions.remove(invoice)}
              className="text-red-700 focus:text-red-700"
            >
              <Trash2 className="mr-2 size-4" />
              Delete
            </DropdownMenuItem>
            {!able.remove.allowed && able.remove.reason && (
              <p className="max-w-[15rem] px-2 pb-1.5 pt-0.5 text-[11px] leading-snug text-[#062B1A]/45">
                {able.remove.reason}
              </p>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
